import React, { useRef, useEffect, useState } from 'react';
import Loading from '../Loading/Loading';
import ProductCard, { Product } from '../CustomCard/ProductCard';
import { CircularProgress } from '@mui/material';

interface ProductListProps {
	products: Product[];
	searching: boolean;
	loadingProducts: boolean;
	loadMore: () => void;
	handleSpecificProductSelect: (product: Product) => void;
	showDetails: (product: Product) => void;
	style: any;
	totalCount: number;
	noCheckbox?: boolean;
	view?: 'order' | 'default';
	increaseQuantity: any;
	decreaseQuantity: any;
	recommendedQuantity: any;
	onCounterChange?: any;
	handleRowClick?: any;

	handleEditClick?: any;
	handleUpdate?: any;
	handleEditClose?: any;
	StyledTextFieldCustomerPrice?: any;
	handleCustomerPrice?: any;
	showStaticQuantity?: boolean;
}

const ProductList: React.FC<ProductListProps> = ({
	products,
	searching,
	loadingProducts,
	loadMore,
	handleSpecificProductSelect,
	showDetails,
	style = { overflow: 'auto', height: 'auto' },
	totalCount,
	noCheckbox = false,
	view = 'default',
	increaseQuantity,
	decreaseQuantity,
	recommendedQuantity,
	onCounterChange,
	handleRowClick,
	handleEditClick,
	handleUpdate,
	handleEditClose,
	StyledTextFieldCustomerPrice,
	handleCustomerPrice,
	showStaticQuantity = false,
}) => {
	const loader = useRef<HTMLDivElement | null>(null);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	useEffect(() => {
		let observer: IntersectionObserver;

		if (loader.current) {
			observer = new IntersectionObserver(
				(entries) => {
					if (
						entries[0].isIntersecting &&
						!loadingProducts &&
						products.length < totalCount &&
						!isLoadingMore
					) {
						setIsLoadingMore(true); // Prevent further calls until this one is processed
						loadMore();
					}
				},
				{ threshold: 1.0 }
			);
			observer.observe(loader.current);
		}

		return () => {
			if (observer && loader.current) {
				observer.disconnect();
			}
		};
	}, [loader.current, loadingProducts, products.length, totalCount, isLoadingMore, loadMore]);

	// Reset isLoadingMore when products change
	useEffect(() => {
		if (isLoadingMore) {
			setIsLoadingMore(false);
		}
	}, [products]);

	return (
		<div style={{ ...style, overflow: loadingProducts ? 'hidden' : 'auto' }}>
			{!searching ? (
				products?.length ? (
					<>
						{products?.map((product) => (
							<ProductCard
								data={product}
								checkBoxSelection={handleSpecificProductSelect}
								showDetails={showDetails}
								showQuantity={false}
								key={product?.id}
								noCheckbox={noCheckbox}
								view={view}
								increaseQuantity={increaseQuantity}
								decreaseQuantity={decreaseQuantity}
								recommendedQuantity={recommendedQuantity}
								onCounterChange={onCounterChange}
								handleRowClick={handleRowClick}
								handleEditClick={handleEditClick}
								handleUpdate={handleUpdate}
								handleEditClose={handleEditClose}
								StyledTextFieldCustomerPrice={StyledTextFieldCustomerPrice}
								handleCustomerPrice={handleCustomerPrice}
								showStaticQuantity={showStaticQuantity}
							/>
						))}
						<div
							style={{
								marginBottom: '16px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
							ref={loader}
						>
							{loadingProducts ? (
								<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
							) : null}
						</div>
					</>
				) : (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: 'calc(100vh - 300px)',
						}}
					>
						No Products Found
					</div>
				)
			) : (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						height: 'calc(100vh - 300px)',
					}}
				>
					<Loading />
				</div>
			)}
		</div>
	);
};

export default ProductList;
